import { AxiosError } from 'axios';
import api from '@/lib/axios';

export interface GoogleCalendarIntegration {
  id: string;
  googleEmail: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Calendar {
  id: string;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  syncedAt?: string;
  deletedAt?: string;
  integrationId: string;
}

export interface CalendarEvent {
  id: string;
  calendarId: string;
  googleEventId: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  attendees: Array<{
    email: string;
    responseStatus?: string;
  }>;
  recurrence?: any;
  isAllDay: boolean;
  status: string;
  colorId?: string | null;
  meetLink?: string | null;
  htmlLink?: string | null;
  organizerEmail?: string | null;
  creatorEmail?: string | null;
  reminders?: Array<{
    method: string;
    minutes: number;
  }>;
  transparency?: string;
  visibility?: string;
  iCalUid?: string | null;
  etag?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  hasDirecionamento?: boolean;
  isCaptacao?: boolean;
  calendar?: {
    id: string;
    name: string;
    color?: string;
  };
}

export interface CalendarEventComment {
  id: string;
  userId: string;
  content: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
  };
  isRead?: boolean;
}

export interface CalendarEventFile {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  googleDriveFileId?: string;
  uploadedBy: string;
  uploadedAt: string;
  webViewLink?: string;
  webContentLink?: string;
  folderPath?: string;
  folderId?: string;
  folderWebViewLink?: string | null;
  uploader?: {
    id: string;
    email: string;
  };
}

export type CalendarEventDirecionamentoFile = CalendarEventFile;

export interface ConnectIntegrationDto {
  code: string;
}

export interface UpdatePermissionsDto {
  blockedUserIds: string[];
}

export interface CreateCommentDto {
  content: string;
  notifyUserIds?: string[];
}

export interface SyncStatus {
  status: string;
  isRunning: boolean;
  startTime?: number;
  lastManualSync?: number | null;
  canSync: boolean;
}

class CalendarService {
  private baseUrl = '/calendar';

  async getAuthUrl(): Promise<{ authUrl: string }> {
    const response = await api.get<{ authUrl: string }>(`${this.baseUrl}/auth-url`);
    return response.data;
  }

  async connectIntegration(dto: ConnectIntegrationDto) {
    const response = await api.post(`${this.baseUrl}/integrations`, dto);
    return response.data;
  }

  async getIntegrations(): Promise<GoogleCalendarIntegration[]> {
    const response = await api.get<GoogleCalendarIntegration[]>(`${this.baseUrl}/integrations`);
    return response.data;
  }

  async disconnectIntegration(integrationId: string) {
    const response = await api.delete(`${this.baseUrl}/integrations/${integrationId}`);
    return response.data;
  }

  async getCalendars(): Promise<Calendar[]> {
    const response = await api.get<Calendar[]>(`${this.baseUrl}/calendars`);
    return response.data;
  }

  async getCalendarPermissions(calendarId: string): Promise<UpdatePermissionsDto> {
    const response = await api.get<UpdatePermissionsDto>(`${this.baseUrl}/calendars/${calendarId}/permissions`);
    return response.data;
  }

  async updateCalendarPermissions(calendarId: string, dto: UpdatePermissionsDto) {
    const response = await api.put(`${this.baseUrl}/calendars/${calendarId}/permissions`, dto);
    return response.data;
  }

  async triggerSync() {
    const response = await api.post(`${this.baseUrl}/calendars/sync`);
    return response.data;
  }

  async getSyncStatus(): Promise<SyncStatus> {
    const response = await api.get<SyncStatus>(`${this.baseUrl}/sync-status`);
    return response.data;
  }

  async getEvents(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get<CalendarEvent[]>(`${this.baseUrl}/events`, { params });
    return response.data;
  }

  async getEventById(eventId: string): Promise<
    CalendarEvent & {
      attendees: Array<{
        id: string;
        userId?: string;
        email: string;
        responseStatus: string;
      }>;
      files: CalendarEventFile[];
      comments: CalendarEventComment[];
    }
  > {
    const response = await api.get(`${this.baseUrl}/events/${eventId}`);
    return response.data;
  }

  async createComment(eventId: string, dto: CreateCommentDto): Promise<CalendarEventComment> {
    const response = await api.post(`${this.baseUrl}/events/${eventId}/comments`, dto);
    return response.data;
  }

  async getEventComments(eventId: string): Promise<CalendarEventComment[]> {
    const response = await api.get<CalendarEventComment[]>(
      `${this.baseUrl}/events/${eventId}/comments`,
    );
    return response.data;
  }

  async markCommentsAsRead(eventId: string) {
    const response = await api.post(`${this.baseUrl}/events/${eventId}/comments/read`);
    return response.data;
  }

  async uploadFile(eventId: string, file: File): Promise<CalendarEventFile> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`${this.baseUrl}/events/${eventId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getEventFiles(eventId: string): Promise<CalendarEventFile[]> {
    const response = await api.get<CalendarEventFile[]>(`${this.baseUrl}/events/${eventId}/files`);
    return response.data;
  }

  async deleteFile(eventId: string, fileId: string) {
    const response = await api.delete(`${this.baseUrl}/events/${eventId}/files/${fileId}`);
    return response.data;
  }

  async getDashboardEvents(): Promise<CalendarEvent[]> {
    const response = await api.get<CalendarEvent[]>(`${this.baseUrl}/events/dashboard`);
    return response.data;
  }

  async getDashboardEventsByEmail(email: string): Promise<CalendarEvent[]> {
    try {
      const response = await api.get<CalendarEvent[]>(
        `${this.baseUrl}/events/dashboard/by-email/${encodeURIComponent(email)}`,
        { skipErrorNotification: true },
      );
      return response.data;
    } catch (e) {
      const err = e as AxiosError;
      if (err.response?.status === 404) return [];
      throw e;
    }
  }

  async getCaptacaoEvents(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get<CalendarEvent[]>(
      `${this.baseUrl}/events/captacao`,
      { params },
    );
    return response.data;
  }

  async uploadDirecionamento(eventId: string, file: File): Promise<CalendarEventFile> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `${this.baseUrl}/events/${eventId}/direcionamento`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  }

  async getDirecionamentoFiles(eventId: string): Promise<CalendarEventDirecionamentoFile[]> {
    const response = await api.get<CalendarEventDirecionamentoFile[]>(
      `${this.baseUrl}/events/${eventId}/direcionamento`,
    );
    return response.data;
  }

  async downloadDirecionamentoFile(eventId: string, fileId: string): Promise<Blob> {
    const response = await api.get(
      `${this.baseUrl}/events/${eventId}/direcionamento/${fileId}/download`,
      {
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async deleteDirecionamentoFile(eventId: string, fileId: string) {
    const response = await api.delete(
      `${this.baseUrl}/events/${eventId}/direcionamento/${fileId}`,
    );
    return response.data;
  }

  async completeEvent(eventId: string) {
    const response = await api.post(`${this.baseUrl}/events/${eventId}/complete`);
    return response.data;
  }
}

export const calendarService = new CalendarService();
